// istanbul ignore file
import * as React from 'react'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import { useAuth } from 'react-oidc-context'
import { connect, useSelector, type ConnectedProps } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { downloadImage } from '../../../device-management/redux/actionCreators'
import SearchBox from '../../../../components/SearchBox'
import LanguageStrings from '../../../../i18n/locales'
import { type RootState } from '../../../../store'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import type { DownloadImageState } from '../../../device-management/redux/downloadImageSlice'
import AddAssetDummy from '../../../../assets/AddDevice/image.png'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { fetchAssetList, fetchSiteInfo } from '../../redux/actionCreator'
import { type AssetListState } from '../../redux/assetListSlice'
import { type SiteInfoState } from '../../redux/siteInfoSlice'
import EnhancedTable, { type Sort } from './AssetTable'
import SiteInformation from '../SiteInformation'
import { Button } from '../../../../components/Button'
import { Grid } from '@mui/material'

export interface State extends SnackbarOrigin {
  open: boolean
}

const AssetListStrings = LanguageStrings().AssetManagementCollectionStrings.AssetListComponent
const DashboardStrings = LanguageStrings().Dashboard

// these values are hardcoded because the hierarchical link is not implemented based on priority. And, that they will be fetched from backend once the links are established
const assetTypeId = 'fdd42da4-4a13-4f57-9e1a-208d95c7f215'

const SiteInfoStyle: React.CSSProperties = {
  width: '99%',
  color: '#F0F0F0',
  padding: '1em',
  backgroundColor: '#272727',
  borderRadius: '5px',
  fontWeight: '500',
  marginBottom: '25px'
}
const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}
const searchBoxComponent: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between'
}

function AssetList (props: PropsFromRedux): JSX.Element {
  const { parentAssetId } = useParams() as { parentAssetId: string }
  const [imageBinaryData, setImageBinaryData] = React.useState<any>(AddAssetDummy)
  const [searchValue, setSearchValue] = React.useState('')
  const rowsPerPage = 10
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: '' })
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()

  const navigate = useNavigate()

  /* istanbul ignore next */
  const handleSearch = (): void => {
    setPage(1)
    props.fetchAssetList(parentAssetId, assetTypeId, page, rowsPerPage, searchValue, sort.order, parentAssetId, sort.orderBy) // site is only the parent of asset
  }

  function handleBreadCrumbsClick (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    if (!operationPermissions.updated) {
      props.getOperationPermissions(auth.user?.profile.sub as string)
    }
  }, [])

  React.useEffect(() => {
    props.fetchSiteInfo(parentAssetId)
    props.downloadImage(parentAssetId, '', '')
  }, [])

  React.useEffect(() => {
    if (props?.downloadImageState?.downloadImage?.binaryData !== '') {
      setImageBinaryData(props?.downloadImageState?.downloadImage)
    }
  }, [props?.downloadImageState?.downloadImage?.binaryData])

  return (
    <div>
      <div role='presentation' data-testid='breadcrumbs' onClick={handleBreadCrumbsClick} className='AmiHeading'>
        <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
          onClick={() => { navigate(-1) }}
        />
        <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
          <Link aria-label='navBreadcrumb' className='assetmgmtli' data-testid="bc-link-1" underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
            {DashboardStrings.HOME}
          </Link>
          <Link aria-label='navBreadcrumb1' className='assetmgmtli' data-testid="bc-link-2" underline='none' color='inherit' onClick={() => { navigate('/site-list') }} >
            {DashboardStrings.SiteManagement}
          </Link>
          <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{DashboardStrings.AssetManagement}</Typography>
        </Breadcrumbs>
      </div>
      <div className='ArrowAssetList'>
        <div>
          <span data-testid='assetHistoryDetails' className='assetMgtAssetlist' style={{ fontWeight: '700', fontSize: '16px' }}>{AssetListStrings.Asset}</span>
        </div>
      </div>
      <br/>
      <div style={SiteInfoStyle}>
        <SiteInformation siteInfo={props.siteInfo} imageBinaryData={imageBinaryData} />
      </div>
      <div className='configurations-table-heading'>
        <h5>{AssetListStrings.AssetList}</h5>
      </div>
      <Grid container style={style} justifyContent={'space-between'} >
        <>
          <Grid item style={searchBoxComponent} xs={6} sm={6} md={4} lg={3} >
            <SearchBox
              sx={{ width: '100%', paddingRight: '0px' }}
              containerStyle={{ width: '100%' }}
              placeholder={AssetListStrings.Search}
              immediate={true}
              emptyEnterFallback={() => {
                props.fetchAssetList(parentAssetId, assetTypeId, page, rowsPerPage, searchValue, sort.order, parentAssetId, sort.orderBy)
              }}
              searchVal={searchValue}
              setSearchVal={setSearchValue}
              performAction={handleSearch}/>
          </Grid>
          <Grid item xs={6} sm={6} md={4} lg={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <>
  {
              operationPermissions.permissions.can_add_tiq_assets
                ? <>
                    <Button sx={{ margin: '10px 17px', padding: '6px 20px' }} onClick={() => {
                      navigate(`/create-asset/${parentAssetId}`)
                    }} width='fit-content' dataTestId='create-asset-btn'>{AssetListStrings.AddNewAsset}</Button>
                  </>
                : <></>
            }
            </>
          </Grid>
          <Grid item xs={12}>
          <EnhancedTable
            siteId={parentAssetId}
            assetTypeId={assetTypeId}
            searchVal={searchValue}
            parentAssetId={parentAssetId}
            page={page}
            rowsPerPage={rowsPerPage}
            sort={sort}
            setPage={setPage}
            setSort={setSort}
            performAction={() => {
              props.fetchAssetList(parentAssetId, assetTypeId, page, rowsPerPage, searchValue, sort.order, parentAssetId, sort.orderBy)
            }}/>
            </Grid>
        </>
      </Grid>
    </div>
  )
}

interface DispatchToProps {
  fetchAssetList: (
    siteId: string,
    siteTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => void
  getOperationPermissions: (sub: string) => void
  fetchSiteInfo: (parentAssetId: string) => void
  downloadImage: (assetId: string, assetTypeId: string, locale: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchAssetList: (
    siteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => dispatch(fetchAssetList(siteId, assetTypeId, PageNumber, PageItems, searchKey, SortParams, parentAssetId, sortField)),
  downloadImage: (assetId: string, assetTypeId: string, locale: string) =>
    dispatch(downloadImage(assetId, assetTypeId, locale)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub)),
  fetchSiteInfo: (parentAssetId: string) => dispatch(fetchSiteInfo(parentAssetId))
})

interface StateToProps {
  assetList: AssetListState
  grandParentBreadcrumb: string
  downloadImageState: DownloadImageState
  siteInfo: SiteInfoState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  assetList: state.assetManagementAssetList,
  siteInfo: state.assetManagementSiteInfo,
  grandParentBreadcrumb: state.grandParentBreadcrumb,
  downloadImageState: state.downloadImageState
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AssetList)
